/**
 * Product Upsell Handlers
 * Global functions for handling product upsell add to cart and variant selection
 */

// Handle variant selection change
window.handleProductUpsellVariantChange = function (selectElement, formId) {
  const form = document.getElementById(formId);
  if (!form) {
    console.error("Form not found:", formId);
    return;
  }

  const productJsonEl = document.getElementById("ProductJson-" + formId);
  if (!productJsonEl) {
    console.error("Product JSON not found for form:", formId);
    return;
  }

  try {
    const product = JSON.parse(productJsonEl.textContent);
    const selects = form.querySelectorAll('select[name^="options["]');
    const selectedOptions = Array.from(selects).map((s) => s.value);

    // Find matching variant
    const variant = product.variants.find((v) => selectedOptions.every((opt, i) => v.options[i] === opt));

    if (variant) {
      const hiddenInput = form.querySelector('input[name="id"]');
      if (hiddenInput) {
        hiddenInput.value = variant.id;
      }

      // Update price if displayed
      const priceElement = form.querySelector(".upsell-price");
      if (priceElement && variant.price) {
        const currency = document.querySelector('meta[name="currency"]')?.content || "USD";
        const formattedPrice = formatMoney(variant.price, currency);
        priceElement.textContent = formattedPrice;
      }

      // Update product image if variant has one
      const imgEl = form.querySelector(".upsell-product-img");
      if (imgEl && variant.featured_image && variant.featured_image.src) {
        imgEl.src = variant.featured_image.src.replace(/(\.\w+)\?/, "$1?width=150&");
        imgEl.alt = variant.featured_image.alt || "";
      }
    }
  } catch (error) {
    console.error("Error handling variant change:", error);
  }
};

// Handle add to cart
window.handleProductUpsellAdd = async function (buttonElement, formId) {
  const form = document.getElementById(formId);
  if (!form) {
    console.error("Form not found:", formId);
    return;
  }

  const variantId = form.querySelector('input[name="id"]')?.value;
  if (!variantId) {
    console.error("No variant ID found");
    return;
  }

  // Disable button and show loading state
  const originalHTML = buttonElement.innerHTML;
  buttonElement.disabled = true;
  buttonElement.classList.add("is-loading");
  buttonElement.innerHTML = '<span class="button-spinner"></span>';

  try {
    // Add to cart
    const response = await fetch(window.routes.cart_add_url + ".js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to add to cart");
    }

    // Try to parse as JSON, handle errors gracefully
    let data;
    try {
      const text = await response.text();
      data = JSON.parse(text);
    } catch (parseError) {
      console.error("Failed to parse cart response:", parseError);
      throw new Error("Invalid cart response");
    }

    // Show success state
    buttonElement.classList.remove("is-loading");
    buttonElement.classList.add("upsell-add-success");
    buttonElement.innerHTML = "✓ Added!";

    // Reset button after 2 seconds
    setTimeout(() => {
      buttonElement.classList.remove("upsell-add-success");
      buttonElement.innerHTML = originalHTML;
      buttonElement.disabled = false;
    }, 2000);

    // Update cart count bubble
    fetch("/cart.js")
      .then((r) => r.json())
      .then((cart) => {
        document.querySelectorAll(".cart-count-bubble, [data-cart-count]").forEach((el) => {
          el.textContent = cart.item_count;
        });
      })
      .catch(() => {});

    // Refresh cart drawer then auto-match upsell variants
    const cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer) {
      fetch(`${window.routes.cart_url}?section_id=cart-drawer`)
        .then((r) => r.text())
        .then((html) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, "text/html");
          const newCartDrawer = doc.querySelector("cart-drawer");
          if (newCartDrawer) {
            cartDrawer.innerHTML = newCartDrawer.innerHTML;
            // Auto-match upsell variants after cart drawer HTML is updated
            autoMatchCartUpsellVariants();
          }
        })
        .catch(() => {});
    }
  } catch (error) {
    console.error("Error adding to cart:", error);

    // Show error state
    buttonElement.classList.remove("is-loading");
    buttonElement.classList.add("upsell-add-error");
    buttonElement.innerHTML = "✗ Error";

    // Reset button after 2 seconds
    setTimeout(() => {
      buttonElement.classList.remove("upsell-add-error");
      buttonElement.innerHTML = originalHTML;
      buttonElement.disabled = false;
    }, 2000);
  }
};

/**
 * Auto-match cart drawer upsell variant dropdowns to match cart item variants.
 * For each upsell form, fetches product data for cart items to find shared
 * option names (e.g. "Color", "Size") and pre-selects the matching values.
 */
window.autoMatchCartUpsellVariants = async function () {
  const forms = document.querySelectorAll(".upsell-form");
  if (!forms.length) return;

  try {
    const cartResp = await fetch("/cart.js");
    const cart = await cartResp.json();
    if (!cart.items || !cart.items.length) return;

    // Collect option name → value from cart items by fetching each product's JSON
    const cartOptionValues = {};
    const fetchedHandles = {};

    for (const item of cart.items) {
      const handle = item.handle;
      if (!handle || fetchedHandles[handle]) continue;
      fetchedHandles[handle] = true;

      try {
        const prodResp = await fetch("/products/" + handle + ".js");
        if (!prodResp.ok) continue;
        const product = await prodResp.json();

        // Find this specific variant in the product
        const variant = product.variants.find((v) => v.id === item.variant_id);
        if (!variant || !product.options) continue;

        // Map option names to the variant's selected values
        product.options.forEach((optName, i) => {
          const val = variant["option" + (i + 1)];
          // First cart item wins (most recently added is first in array)
          if (val && !cartOptionValues[optName.toLowerCase().trim()]) {
            cartOptionValues[optName.toLowerCase().trim()] = val;
          }
        });
      } catch (e) {
        /* continue to next item */
      }
    }

    if (!Object.keys(cartOptionValues).length) return;

    // For each upsell form, set dropdowns to matching option values
    forms.forEach((form) => {
      const productJsonEl = document.getElementById("ProductJson-" + form.id);
      if (!productJsonEl) return;

      let upsellProduct;
      try {
        upsellProduct = JSON.parse(productJsonEl.textContent);
      } catch (e) {
        return;
      }

      if (!upsellProduct.variants || upsellProduct.variants.length <= 1) return;

      const selects = form.querySelectorAll('select[name^="options["]');
      let changed = false;

      selects.forEach((select) => {
        const match = select.name.match(/^options\[(.+)\]$/);
        if (!match) return;

        const optName = match[1].toLowerCase().trim();
        const matchValue = cartOptionValues[optName];
        if (!matchValue) return;

        // Find the option element with this value (case-insensitive)
        for (const opt of select.options) {
          if (opt.value.toLowerCase().trim() === matchValue.toLowerCase().trim()) {
            select.value = opt.value;
            changed = true;
            break;
          }
        }
      });

      // Trigger variant change to update the hidden input, price, and image
      if (changed && selects.length > 0) {
        handleProductUpsellVariantChange(selects[0], form.id);
      }
    });
  } catch (e) {
    /* silently fail */
  }
};

// Run auto-match on initial page load (for server-rendered cart drawer)
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", autoMatchCartUpsellVariants);
} else {
  autoMatchCartUpsellVariants();
}

// Also run auto-match whenever the cart drawer is opened or refreshed
document.addEventListener("cart:refresh", function () {
  // Small delay to let the DOM update
  setTimeout(autoMatchCartUpsellVariants, 100);
});

// Helper function to format money
function formatMoney(cents, currency = "USD") {
  const amount = cents / 100;

  // Try to use Shopify's money format if available
  if (typeof Shopify !== "undefined" && Shopify.formatMoney) {
    return Shopify.formatMoney(cents, Shopify.money_format || "{{amount}}");
  }

  // Fallback formatting
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
  }).format(amount);
}
